import React, { useState } from "react";
import { Container } from "../../theme";
import { SocialItem } from "../../hooks/usePersistentContent";
import ProgressiveImage from "../shared/ProgressiveImage";

export interface NavbarProps {
  brand: string;
  logo?: string;
  nav: string[];
  social: SocialItem[];
}
// Map nav labels to section IDs
export const NAV_LINK_TARGETS: readonly string[] = ["#hero", "#about", "#productsview", "#why", "#team", "#carousel", "#reviews", "#contact"] as const;

export const Navbar: React.FC<NavbarProps> = ({ brand, logo, nav, social = [] }) => {
  const [open, setOpen] = useState<boolean>(false);
  const isRootPath: boolean = typeof window !== "undefined" ? window.location.pathname === "/" : true;
  const navbarBackground: string = "rgba(255, 248, 241, 0.98)";
  const mobileMenuBackground: string = "rgba(255, 253, 249, 0.96)";

  const normalizeHref = (target: string): string => {
    if (!target.startsWith("#")) {
      return target;
    }
    return isRootPath ? target : `/${target}`;
  };
  return (
    <header
      role="banner"
      className="fixed top-0 left-0 right-0 z-[1000] w-full border-b border-border/80 shadow-sm"
      style={{ background: navbarBackground }}
    >
      <Container className="mx-auto flex items-center justify-between h-16">
        <a
          href={isRootPath ? "#top" : "/#top"}
          className="flex items-center gap-2 brand-font tracking-wide text-[26px] text-brand"
        >
          <img src={logo || "/assets/site_logo.png"} alt="Logo" className="h-14 w-auto" width={56} height={56} />
          <span className="font-bold">{brand}</span>
        </a>
        <nav aria-label="ניווט ראשי" className="hidden lg:flex items-center gap-6 xl:gap-8 menu-font text-[16px] xl:text-[18px] text-ink-dark">
          {nav.map((label, idx) => {
            const href: string = NAV_LINK_TARGETS[idx] ?? "#";
            const normalisedHref: string = normalizeHref(href);
            return (
              <a key={href} href={normalisedHref} className="hover:opacity-80 whitespace-nowrap">{label}</a>
            );
          })}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {social.map((item) => (
            <a 
              key={item.name} 
              href={item.url} 
              aria-label={item.ariaLabel}
              className="inline-flex items-center justify-center w-7 h-7 hover:opacity-80 transition-opacity"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ProgressiveImage
                src={`/assets/social-logos/${item.icon}.png`}
                alt={item.name}
                wrapperClassName="h-6 w-6"
                className="h-full w-full object-contain"
                loading="lazy"
                decoding="async"
                sizes="24px"
                width={24}
                height={24}
              />
            </a>
          ))}
        </div>
        <button
          className="lg:hidden text-2xl text-ink"
          onClick={() => setOpen(v => !v)}
          aria-label="Menu"
        >
          ☰
        </button>
      </Container>
      {open && (
        <div
          className="lg:hidden border-t border-border"
          style={{ background: mobileMenuBackground, backdropFilter: "blur(10px)" }}
          dir="rtl"
          lang="he"
        >
          <Container className="py-4 flex flex-col gap-3 menu-font text-ink text-right">
            {nav.map((label, idx) => {
              const href: string = NAV_LINK_TARGETS[idx] ?? "#";
              // On mobile, redirect "השירותים שלנו" to /products page instead of #productsview
              const mobileHref: string = href === "#productsview" ? "/products" : normalizeHref(href);
              return (
                <a key={href} href={mobileHref} className="py-2 text-right" onClick={() => setOpen(false)}>{label}</a>
              );
            })}
            <div className="flex items-center justify-center gap-3 pt-2">
              {social.map((item) => (
                <a 
                  key={item.name} 
                  href={item.url} 
                  aria-label={item.ariaLabel}
                  className="inline-flex items-center justify-center w-7 h-7 hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ProgressiveImage
                    src={`/assets/social-logos/${item.icon}.png`}
                    alt={item.name}
                    wrapperClassName="h-6 w-6"
                    className="h-full w-full object-contain"
                    loading="lazy"
                    decoding="async"
                    sizes="24px"
                    width={24}
                    height={24}
                  />
                </a>
              ))}
            </div>
          </Container>
        </div>
      )}
    </header>
  );
};

export default Navbar;


